import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import type { JSX } from 'react';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import MaterialRateDetails from '~pages/Sales/Orders/NewOrderForm/MaterialRateDetails';
import MaterialsV2 from '~pages/Sales/Orders/OrderFormV2/components/MaterialsV2';

import { useOrderFormV3Context } from '../OrderFormV3Context';

/**
 * Renders `form_fields.materials`. Render-only: the embedded MaterialsV2
 * exactly as legacy mounts it (no fork; material/quantity/unitOfMeasure live
 * inside the embed), the show/hide PseudoLink, and the ref-driven
 * MaterialRateDetails block — handed to the embed as its primary-material rates
 * slot, because rates attach to the order's single scalar material and so belong
 * inside that material's card rather than below the whole multi-material list.
 * The block's visibility state, the toggle, the permission gate and the
 * mount-time anchors all arrive via context: no watch hooks, no flag reads, no
 * effects in this file.
 *
 * MaterialRateDetails must stay mounted for the shell's submit path to read its
 * imperative handle, so the slot is always passed — the embed hides its body
 * through the `isVisible` prop, never by unmounting.
 *
 * The project's material rates go down as a plain prop: the embed is shared with
 * the legacy form, which has no v3 context to read them from.
 */
export function MaterialsPanel(): JSX.Element {
  const {
    canManageMaterialRates,
    defaultOrder,
    isMaterialRateRequired,
    isMaterialRatesBlockVisible,
    materialRateDetailsRef,
    materialRateInitialIds,
    itemizeMaterialRateDefaultOn,
    projectData,
    projectMaterialRates,
    siteFilteredMaterialIds,
    toggleMaterialRatesBlockVisibility,
  } = useOrderFormV3Context();

  return (
    // overflow visible: the material typeaheads render their options inline
    // (disablePortal), so a Card clipping its overflow cuts off the dropdown on
    // the rows nearest the bottom edge.
    <Card sx={{ p: 2, mb: 2, overflow: 'visible' }}>
      <Typography variant="form-section-title" sx={{ mb: 1 }}>
        {t('form_fields.materials')}
      </Typography>

      <MaterialsV2
        allowedMaterialIds={siteFilteredMaterialIds}
        parent={projectData ?? undefined}
        order={defaultOrder ?? undefined}
        projectMaterialRates={projectMaterialRates}
        primaryMaterialRatesSlot={
          <>
            <PseudoLink
              action={toggleMaterialRatesBlockVisibility}
              title={
                isMaterialRatesBlockVisible
                  ? t('order.form_v2.hide_material_rates')
                  : t('order.form_v2.show_material_rates')
              }
              sx={{
                display: 'inline-block',
                ...(isMaterialRatesBlockVisible ? { mb: 3 } : {}),
              }}
            />

            <MaterialRateDetails
              ref={materialRateDetailsRef}
              initialCompanyId={materialRateInitialIds.companyId}
              initialMaterialId={materialRateInitialIds.materialId}
              initialPickupSiteId={materialRateInitialIds.pickupSiteId}
              omitWrapper
              isVisible={canManageMaterialRates && isMaterialRatesBlockVisible}
              projectMaterialRateItemizedDefault={projectData?.materialRateItemized}
              itemizeMaterialRateDefaultOn={itemizeMaterialRateDefaultOn}
              isMaterialRateRequired={isMaterialRateRequired}
            />
          </>
        }
      />
    </Card>
  );
}
